import type { ReaderSettings } from "@bibleunleashed/shared";
import { useSettings } from "@/lib/settings";

function Seg<T extends string | number>({ value, options, onChange }: { value: T; options: { v: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex overflow-hidden rounded-md border border-rule">
      {options.map((o) => (
        <button key={String(o.v)} onClick={() => onChange(o.v)} className={`px-2.5 py-1 text-xs ${o.v === value ? "bg-paper-3 text-ink" : "text-ink-2"}`} aria-pressed={o.v === value}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} className={`relative h-[18px] w-8 rounded-full transition ${on ? "bg-accent" : "bg-rule"}`}>
      <span className={`absolute top-0.5 size-3.5 rounded-full bg-white transition-[left] ${on ? "left-4" : "left-0.5"}`} />
    </button>
  );
}

export function TextSettings({ onClose }: { onClose: () => void }) {
  const { settings, update } = useSettings();
  const row = "flex items-center justify-between py-[7px] text-[13px]";
  const lbl = "mt-1.5 mb-0.5 text-[11px] uppercase tracking-[0.08em] text-ink-3";
  const toggles: { key: keyof ReaderSettings; label: string }[] = [
    { key: "verseNumbers", label: "Verse numbers" },
    { key: "headings", label: "Section headings" },
    { key: "footnotes", label: "Footnotes" },
    { key: "crossrefs", label: "Cross-references" },
    { key: "redLetter", label: "Words of Christ in red" },
    { key: "highlights", label: "My highlights" },
  ];
  return (
    <div role="dialog" aria-label="Text settings" className="absolute top-2 right-2 z-20 w-[280px] rounded-lg border border-rule bg-paper px-4 py-3.5 shadow-pop" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <div className={lbl}>Type</div>
        <button onClick={onClose} className="text-xs text-ink-3 hover:text-ink">Done</button>
      </div>
      <div className={row}><span>Font</span><Seg value={settings.font} options={[{ v: "serif", label: "Serif" }, { v: "sans", label: "Sans" }]} onChange={(font) => update({ font })} /></div>
      <div className={row}>
        <span>Size</span>
        <div className="flex overflow-hidden rounded-md border border-rule">
          <button className="px-2.5 py-1 text-xs text-ink-2" onClick={() => update({ size: Math.max(-2, settings.size - 1) })} aria-label="Smaller text">A−</button>
          <span className="px-2.5 py-1 text-xs tabular-nums text-ink">{settings.size >= 0 ? `+${settings.size}` : settings.size}</span>
          <button className="px-2.5 py-1 text-xs text-ink-2" onClick={() => update({ size: Math.min(3, settings.size + 1) })} aria-label="Larger text">A+</button>
        </div>
      </div>
      <div className={row}><span>Theme</span><Seg value={settings.theme} options={[{ v: "system", label: "System" }, { v: "light", label: "Light" }, { v: "dark", label: "Dark" }]} onChange={(theme) => update({ theme })} /></div>
      <div className={lbl}>Show</div>
      {toggles.map((t) => (
        <div key={t.key} className={row}>
          <span>{t.label}</span>
          <Toggle on={Boolean(settings[t.key])} onChange={(v) => update({ [t.key]: v })} label={t.label} />
        </div>
      ))}
    </div>
  );
}
